<template>
  <div class="container mt-4">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">
          <i class="fas fa-calendar-alt text-primary me-2"></i>
          Work Order Schedules
        </h2>
        <span class="badge bg-light text-secondary fw-normal">{{ pagination.total_rows || 0 }} schedules</span>
      </div>
      <div class="header-right">
        <button class="btn btn-primary btn-sm" @click="openCreateModal('createModal')">
          <i class="fas fa-plus me-1"></i> Add Schedule
        </button>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body p-3">
        <!-- Modal -->
        <FormModal ref="createModal" title="Create New Schedule" id="create_schedule_form_modal">
          <CreateSchedule 
            ref="CreateScheduleForm" 
            @created="onCreated" 
            @close="closeCreateModal('createModal')" 
          />
        </FormModal>

        <!-- Loading State -->
        <LoadingBar v-if="loading" message="Loading Schedules..." />

        <!-- Schedules List -->
        <div v-else class="schedule-list">
          <div v-for="schedule in sortedData" :key="schedule.id" class="schedule-card">
            <!-- Schedule Header -->
            <div class="schedule-header" @click="toggleExpand(schedule.id)">
              <div class="schedule-info">
                <div class="schedule-title">
                  <span class="fw-semibold">{{ schedule.name || 'Schedule' }}</span>
                  <span class="schedule-id">{{ schedule.type }}</span>
                </div>
                <div class="schedule-meta">
                  <span class="meta-item">
                    <i class="fas fa-calendar-day"></i> {{ formatDate(schedule.schedule_date) }}
                  </span>
                  <span class="meta-item">
                    <i class="fas fa-clock"></i> {{ schedule.get_sub_schedules?.length || 0 }} slots
                  </span>
                  <span class="meta-item">
                    <span v-html="getStatusBadge(schedule.status)">
                      
                    </span>
                  </span>
                </div>
              </div>
              <div class="schedule-actions">
                <button class="btn btn-sm btn-ghost" @click.stop="toggleExpand(schedule.id)" :title="expandedId === schedule.id ? 'Collapse' : 'Expand'">
                  <i :class="expandedId === schedule.id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                </button>
                <button class="btn btn-sm btn-ghost text-danger" @click.stop="deleteSchedule(schedule.id)" title="Delete Schedule">
                  <i class="fas fa-trash"></i>
                </button>
                <div class="dropdown d-inline-block">
                  <button class="btn btn-sm btn-ghost" type="button" data-bs-toggle="dropdown">
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                  <ul class="dropdown-menu">
                    <li><a class="dropdown-item" href="#"><i class="fas fa-eye me-2"></i>View Details</a></li>
                    <li><a class="dropdown-item" href="#"><i class="fas fa-edit me-2"></i>Edit</a></li>
                    <li><hr class="dropdown-divider"></li>
                    <li><a class="dropdown-item text-danger" @click="deleteSchedule(schedule.id)"><i class="fas fa-trash me-2"></i>Delete</a></li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Sub-Schedules (Time Slots) -->
            <div v-if="expandedId === schedule.id" class="sub-schedule-list">
              <div v-if="schedule.get_sub_schedules && schedule.get_sub_schedules.length > 0">
                <div class="sub-schedule-item" v-for="(slot, index) in schedule.get_sub_schedules" :key="slot.id">
                  <div class="slot-number">{{ index + 1 }}</div>
                  
                  <div class="slot-time">
                    <i class="fas fa-clock text-muted"></i>
                    <span class="sub-schedule-title schedule-start" @click="startInspection(slot.id)">
                      {{ formatDateTime(slot.schedule_date) }}
                      <i class="fas fa-play text-primary ms-2"></i>
                    </span>
                  </div>
                  <div class="slot-status">
                    <span v-html="getStatusBadge(slot.status)">
                    </span>
                  </div>
                  <div class="slot-actions">
                    <button class="btn btn-sm btn-ghost text-danger" @click="deleteSubSchedule(slot.id)" title="Delete Slot">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="no-slots">
                <i class="fas fa-clock text-muted me-2"></i>
                <span>No time slots added for this schedule</span>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="schedules.length === 0" class="empty-state">
            <i class="fas fa-calendar-plus fa-4x text-muted mb-3"></i>
            <h5 class="text-muted">No Schedules Found</h5>
            <p class="text-muted">Click "Add Schedule" to create your first schedule</p>
            <button class="btn btn-primary btn-sm mt-2" @click="openCreateModal('createModal')">
              <i class="fas fa-plus me-1"></i> Add Schedule
            </button>
          </div>
        </div>

        <!-- Pagination -->
        <Pagination :pagination="pagination" @page-change="changePage" />
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateSchedule from '@/views/WO/Schedule/CreateSchedule.vue'
import ListTable from '@/components/utils/ListTable.vue'
import { getStatusBadge } from '@/utils/getStatusBadge'

export default {
  name: 'ScheduleList',
  components: { 
    Pagination, 
    LoadingBar, 
    FormModal, 
    CreateSchedule, 
    ListTable 
  },
  data() {
    return {
      schedules: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1
      },
      loading: false,
      sortColumn: 'name',
      sortOrder: 'asc',
      expandedId: null
    }
  },
  computed: {
    sortedData() {
      let result = [...this.schedules];
      
      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];
          
          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';
          
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          
          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      return result;
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    getStatusBadge,
    
    startInspection(id) {
      this.$router.push({ name: 'ConductSchedule', params: { id } })
    },
    
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatDateTime(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async deleteSchedule(id) {
      if (!confirm('Are you sure you want to delete this schedule? This will also delete all associated sub-schedules.')) {
        return
      }

      try {
        this.loading = true
        const result = await form_api_submit({
          url: 'wo/schedule/delete/' + id,
          method: 'DELETE',
          show_success_toast: true,
          show_error_toast: true
        })

        if (result.success) {
          // this.$toast('Schedule deleted successfully!', true)
          this.pagination.current_page = 1
          await this.fetchList()
        }
      } catch (error) {
        console.error('Error deleting schedule:', error)
        // this.$toast('Failed to delete schedule', false)
      } finally {
        this.loading = false
      }
    },

    async deleteSubSchedule(id) {
      if (!confirm('Are you sure you want to delete this time slot?')) {
        return
      }

      try {
        this.loading = true
        const result = await form_api_submit({
          url: 'wo/schedule/delete/' + id,
          method: 'DELETE',
          show_success_toast: true,
          show_error_toast: true
        })

        if (result.success) {
          this.$toast('Time slot deleted successfully!', true)
          this.pagination.current_page = 1
          await this.fetchList()
        }
      } catch (error) {
        console.error('Error deleting time slot:', error)
        this.$toast('Failed to delete time slot', false)
      } finally {
        this.loading = false
      }
    },

    getStatusClass(status) {
      const classes = {
        pending: 'badge bg-warning',
        active: 'badge bg-success',
        completed: 'badge bg-info',
        cancelled: 'badge bg-danger'
      }
      return classes[status?.toLowerCase()] || 'badge bg-secondary'
    },

    getSlotStatusClass(status) {
      const classes = {
        pending: 'badge bg-warning',
        completed: 'badge bg-success',
        missed: 'badge bg-danger',
        cancelled: 'badge bg-secondary'
      }
      return classes[status?.toLowerCase()] || 'badge bg-secondary'
    },

    openCreateModal(ref) {
      this.$refs[ref].showModal()
    },

    closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },

    async onCreated() {
      this.pagination.current_page = 1
      await this.fetchList()
    },

    async fetchList() {
      this.loading = true

      const result = await form_api_submit({
        url: 'wo/schedule/list/' + this.$route.params.id,
        method: 'GET',
        data: {
          page: this.pagination.current_page,
        },
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.schedules = result.data.list
        this.pagination = result.data.pagination
      }

      this.loading = false
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'fas fa-sort text-muted'
      }
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    }
  }
}
</script>

<style scoped>
/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 4px 16px 4px;
  border-bottom: 1px solid #f0f0f0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Schedule List */
.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}

.schedule-card {
  background: white;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.25s ease;
}

.schedule-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.2s;
}

.schedule-header:hover {
  background: #fafbfc;
}

.schedule-info {
  flex: 1;
  min-width: 0;
}

.schedule-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.schedule-title .fw-semibold {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}

.schedule-id {
  font-size: 11px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 1px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.schedule-meta {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #64748b;
}

.meta-item i {
  font-size: 11px;
  color: #94a3b8;
}

.schedule-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-ghost {
  background: transparent;
  border: none;
  padding: 4px 6px;
  color: #94a3b8;
  transition: all 0.2s;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-ghost:hover {
  background: #f1f5f9;
  color: #334155;
}

.btn-ghost i {
  font-size: 14px;
}

.btn-ghost.text-danger:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Sub-Schedule List */
.sub-schedule-list {
  padding: 10px 18px 18px 18px;
  border-top: 1px solid #f0f0f0;
  background: #ffffff;
}

.sub-schedule-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: #fafbfc;
  border-radius: 8px;
  margin-bottom: 6px;
  border: 1px solid #f0f0f0;
  transition: all 0.15s;
}

.sub-schedule-item:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.sub-schedule-item:last-child {
  margin-bottom: 0;
}

.slot-number {
  width: 26px;
  height: 26px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  flex-shrink: 0;
}

.slot-time {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #1a202c;
}

.sub-schedule-title {
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s;
}

.sub-schedule-title:hover {
  background: #f1f5f9;
}

.sub-schedule-title i {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.sub-schedule-title:hover i {
  opacity: 1;
}

.slot-status {
  flex-shrink: 0;
}

.slot-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.slot-actions .btn-ghost {
  width: 28px;
  height: 28px;
}

.no-slots {
  padding: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
}

/* Badge styles */
.badge {
  font-size: 11px;
  padding: 3px 12px;
  border-radius: 20px;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px dashed #e2e8f0;
}

.empty-state h5 {
  color: #475569;
  margin-bottom: 4px;
}

.empty-state p {
  color: #94a3b8;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .header-left {
    flex-wrap: wrap;
  }
  
  .schedule-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .schedule-meta {
    gap: 10px;
  }
  
  .schedule-actions {
    margin-left: auto;
  }
  
  .sub-schedule-item {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .slot-time {
    flex: 1 1 100%;
  }
}
</style>